import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import {
  Upload, Link, ChevronRight, CheckCircle2, XCircle, AlertTriangle,
  Loader2, FileSpreadsheet, RefreshCw, History, Play, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const STEPS = ['source', 'mapping', 'preview', 'running', 'results'];
const STEP_LABELS = ['Data Source', 'Column Mapping', 'Preview & Validate', 'Import', 'Results'];
const TAB_ORDER = ['CUSTOMERS', 'UNITS', 'REPAIRS', 'INVOICES', 'PM_DOT'];
const TAB_LABELS = { CUSTOMERS: 'Customers', UNITS: 'Units / Trucks', REPAIRS: 'Repair History', INVOICES: 'Invoices', PM_DOT: 'PM / DOT' };
const REQUIRED = { UNITS: ['unit_number'], REPAIRS: ['unit_number', 'date', 'description'], INVOICES: ['invoice_number', 'amount'], PM_DOT: ['unit_number', 'record_type'], CUSTOMERS: [] };

const PAYMENT_COLORS = { SUCCESS: 'text-[#57EB31]', PARTIAL: 'text-[#3B82F6]', FAILED: 'text-red-400', DRY_RUN: 'text-blue-400', PROCESSING: 'text-[#3B82F6]' };

export default function ImportCenter() {
  const [step, setStep] = useState('source');
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [sourceMode, setSourceMode] = useState('file');
  const [sheetUrl, setSheetUrl] = useState('');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [jobId, setJobId] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/customers', { params: { limit: 200 } }).then(r => setCustomers(r.data.items || []));
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadHistory = async () => {
    const r = await api.get('/import-center/jobs', { params: { customer_id: customerId || undefined } });
    setHistory(r.data);
    setShowHistory(true);
  };

  const handleParse = async () => {
    if (!customerId) return toast.error('Select a customer first');
    if (sourceMode === 'file' && !file) return toast.error('Select a file');
    if (sourceMode === 'sheet' && !sheetUrl) return toast.error('Paste a Google Sheet URL');

    setParsing(true);
    try {
      const fd = new FormData();
      if (sourceMode === 'file' && file) fd.append('file', file);
      if (sourceMode === 'sheet') fd.append('sheet_url', sheetUrl);
      const res = await api.post('/import-center/parse', fd);
      setParsedData(res.data);
      // Pre-fill mappings with auto-detected
      const initMappings = {};
      for (const [tabName, tabData] of Object.entries(res.data.tabs)) {
        initMappings[tabName] = tabData.auto_mapping || {};
      }
      setMappings(initMappings);
      setStep('mapping');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse source');
    } finally {
      setParsing(false);
    }
  };

  const handleRunJob = async (isDryRun) => {
    if (!customerId) return;
    setStep('running');
    try {
      const fd = new FormData();
      if (sourceMode === 'file' && file) fd.append('file', file);
      fd.append('payload', JSON.stringify({
        customer_id: customerId,
        mappings,
        is_dry_run: isDryRun,
        sheet_url: sourceMode === 'sheet' ? sheetUrl : null,
        tabs_data: parsedData ? Object.fromEntries(
          Object.entries(parsedData.tabs).map(([k, v]) => [k, { headers: v.headers, rows: v.sample_rows }])
        ) : null,
        file_hash: parsedData?.file_hash,
        source_type: parsedData?.source_type,
      }));
      const res = await api.post('/import-center/jobs', fd);

      if (res.data.already_imported) {
        toast.warning('This file was already imported. Showing previous results.');
        setJobData(res.data);
        setJobId(res.data.job_id);
        setStep('results');
        return;
      }

      setJobId(res.data.job_id);
      pollRef.current = setInterval(async () => {
        const status = await api.get(`/import-center/jobs/${res.data.job_id}`);
        setJobData(status.data);
        if (status.data.status !== 'PROCESSING') {
          clearInterval(pollRef.current);
          setStep('results');
        }
      }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
      setStep('preview');
    }
  };

  const reset = () => {
    setStep('source');
    setFile(null); setSheetUrl(''); setParsedData(null);
    setMappings({}); setJobId(null); setJobData(null);
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const tabsPresent = parsedData ? Object.keys(parsedData.tabs) : [];
  const selectedCustomer = customers.find(c => c.id === customerId);

  // ── Validation summary for preview step
  const getValidationSummary = () => {
    if (!parsedData) return [];
    return tabsPresent.map(tabName => {
      const tab = parsedData.tabs[tabName];
      const m = mappings[tabName] || {};
      const unmapped = REQUIRED[tabName]?.filter(f => !m[f]) || [];
      return {
        tabName, rows: tab.total_rows,
        hasErrors: unmapped.length > 0,
        errors: unmapped.map(f => `Required field "${f}" not mapped`),
      };
    });
  };

  const fmt = (n) => new Intl.NumberFormat('en-US').format(n || 0);

  const handleBulkSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await api.post('/import/bulk-production?secret=MM-BULK-2026-STEFAN');
      setSyncResult(res.data);
      toast.success(`Synced! ${res.data.customers_created} customers, ${res.data.beyond_trucks_created || 0} trucks, ${res.data.units_created || 0} trailers`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6 space-y-5 animate-fade-in" data-testid="import-center-page">
      {/* Bulk Sync Banner */}
      <div className="p-4 rounded-sm border flex items-center justify-between" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <div>
          <p className="text-sm font-['Manrope'] font-bold" style={{ color: 'var(--text-primary)' }}>Sync Production Data</p>
          <p className="text-xs font-['IBM_Plex_Sans'] mt-0.5" style={{ color: 'var(--text-muted)' }}>
            One-click import of all pre-loaded customers & units to this environment
          </p>
        </div>
        <div className="flex items-center gap-3">
          {syncResult && (
            <span className="text-xs font-['JetBrains_Mono']" style={{ color: '#57EB31' }}>
              {syncResult.customers_created} new customers · {syncResult.beyond_trucks_created || 0} trucks · {syncResult.units_created || 0} trailers
            </span>
          )}
          <button onClick={handleBulkSync} disabled={syncing} data-testid="bulk-sync-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-sm font-['Manrope'] font-bold text-sm transition-colors disabled:opacity-50"
            style={{ background: '#57EB31', color: '#0F172A' }}>
            {syncing ? <><Loader2 size={14} className="animate-spin" /> Syncing...</> : <><RefreshCw size={14} /> Sync Now</>}
          </button>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-[#2D3F5E]/60">
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// DATA MIGRATION</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Import Center</h1>
          <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mt-0.5">Import customer history from Google Sheets or XLSX — per customer, safe, idempotent</p>
        </div>
        <button onClick={loadHistory}
          className="flex items-center gap-2 px-3 py-2 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm text-xs hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors font-['JetBrains_Mono']">
          <History size={13} /> Import History
        </button>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-['JetBrains_Mono'] font-bold uppercase tracking-wider transition-all ${
              step === s ? 'bg-[#3B82F6] text-white' :
              STEPS.indexOf(step) > i ? 'text-[#57EB31] bg-[#57EB31]/10 border border-[#57EB31]/30' :
              'text-[#5C6A7A] bg-[#1E293B] border border-[#2D3F5E]'
            }`}>
              {STEPS.indexOf(step) > i ? <CheckCircle2 size={10} /> : <span>{i+1}</span>}
              {STEP_LABELS[i]}
            </div>
            {i < STEPS.length - 1 && <ChevronRight size={12} className="text-[#2D3F5E]" />}
          </div>
        ))}
      </div>

      {/* ── STEP: SOURCE ──────────────────────────────────────────────────── */}
      {step === 'source' && (
        <div className="max-w-2xl space-y-5">
          {/* Customer select */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Step 1 — Select Customer</p>
            <select value={customerId} onChange={e => setCustomerId(e.target.value)}
              className="w-full h-11 px-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
              data-testid="customer-select">
              <option value="">Select customer to import data for...</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
            {selectedCustomer && (
              <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-2">
                {selectedCustomer.onboarding_state} · {selectedCustomer.fleet_size || '?'} units
              </p>
            )}
          </div>

          {/* Source mode */}
          <div className="card-industrial p-5">
            <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest text-[#5C6A7A] mb-3">Step 2 — Choose Data Source</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[
                { mode: 'file', icon: Upload, label: 'Upload XLSX / CSV', desc: 'Upload your exported spreadsheet' },
                { mode: 'sheet', icon: Link, label: 'Google Sheet URL', desc: 'Paste the Google Sheet link' },
              ].map(({ mode, icon: Icon, label, desc }) => (
                <button key={mode} type="button" onClick={() => setSourceMode(mode)}
                  className={`p-4 rounded-sm border text-left transition-all ${sourceMode === mode ? 'border-[#3B82F6] bg-[#3B82F6]/8' : 'border-[#2D3F5E] hover:border-[#3B82F6]/40'}`}>
                  <Icon size={18} className={`mb-2 ${sourceMode === mode ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`} />
                  <p className={`text-sm font-['Manrope'] font-bold ${sourceMode === mode ? 'text-[#3B82F6]' : 'text-white'}`}>{label}</p>
                  <p className="text-xs text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-0.5">{desc}</p>
                </button>
              ))}
            </div>

            {sourceMode === 'file' && (
              <div className="border-2 border-dashed border-[#2D3F5E] rounded-sm p-6 text-center cursor-pointer hover:border-[#3B82F6]/50 transition-colors"
                onClick={() => fileRef.current?.click()}>
                {file ? (
                  <p className="text-[#57EB31] font-['IBM_Plex_Sans'] text-sm">{file.name} ({(file.size/1024).toFixed(0)}KB)</p>
                ) : (
                  <>
                    <Upload size={24} className="mx-auto mb-2 text-[#5C6A7A]" />
                    <p className="text-sm text-[#B8C0CC] font-['IBM_Plex_Sans']">Click to upload XLSX or CSV</p>
                    <p className="text-xs text-[#5C6A7A] mt-1">Multi-tab XLSX recommended (UNITS, REPAIRS, INVOICES, PM_DOT tabs)</p>
                  </>
                )}
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" data-testid="file-input"
                  onChange={e => setFile(e.target.files[0])} />
              </div>
            )}

            {sourceMode === 'sheet' && (
              <div className="space-y-3">
                <div className="p-3 bg-[#0A0F1A] border border-[#2D3F5E]/60 rounded-sm text-xs font-['IBM_Plex_Sans'] text-[#5C6A7A]">
                  Share your sheet with: <span className="text-[#3B82F6] font-['JetBrains_Mono']">document-ai@millenials-487815.iam.gserviceaccount.com</span>
                </div>
                <input type="url" value={sheetUrl} onChange={e => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full h-10 px-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6]"
                  data-testid="sheet-url-input" />
              </div>
            )}
          </div>

          <button onClick={handleParse} disabled={parsing || !customerId}
            className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] disabled:opacity-50 transition-colors shadow-[0_0_20px_rgba(59,130,246,0.2)]"
            data-testid="parse-btn">
            {parsing ? <><Loader2 size={16} className="animate-spin" />Parsing...</> : <><ChevronRight size={16} />Parse & Detect Columns</>}
          </button>
        </div>
      )}

      {/* ── STEP: MAPPING ─────────────────────────────────────────────────── */}
      {step === 'mapping' && parsedData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">
              Found <strong className="text-white">{tabsPresent.length}</strong> tab(s). Verify column mapping for each. Yellow = auto-detected.
            </p>
            <button onClick={() => setStep('source')} className="text-xs text-[#5C6A7A] hover:text-[#3B82F6] font-['JetBrains_Mono']">← Back</button>
          </div>
          {tabsPresent.map(tabName => {
            const tabInfo = parsedData.tabs[tabName];
            const tabMap = mappings[tabName] || {};
            const allFields = Object.keys(REQUIRED[tabName] ? { ...{ unit_number: '', date: '', description: '' }, ...(Object.fromEntries(Object.keys(tabInfo.auto_mapping || {}).map(k => [k, '']))) } : {});
            const expectedFields = Object.keys((tabInfo.auto_mapping || {}));
            const allExpected = [...new Set([...(REQUIRED[tabName] || []), ...Object.keys(tabInfo.auto_mapping || {})])];

            return (
              <div key={tabName} className="card-industrial p-5">
                <div className="flex items-center gap-3 mb-4">
                  <p className="text-white font-['Manrope'] font-bold">{TAB_LABELS[tabName] || tabName}</p>
                  <span className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A]">{tabInfo.total_rows} rows · {tabInfo.headers.length} columns</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {allExpected.map(field => {
                    const isRequired = (REQUIRED[tabName] || []).includes(field);
                    const currentCol = tabMap[field];
                    const isAutoDetected = !!(tabInfo.auto_mapping || {})[field];
                    return (
                      <div key={field}>
                        <label className={`block text-[9px] font-['JetBrains_Mono'] uppercase mb-1 ${isRequired ? 'text-[#3B82F6]' : 'text-[#5C6A7A]'}`}>
                          {field.replace(/_/g, ' ')}{isRequired ? ' *' : ''}
                        </label>
                        <select value={currentCol || ''} onChange={e => setMappings(p => ({
                          ...p, [tabName]: { ...p[tabName], [field]: e.target.value || undefined }
                        }))}
                          className={`w-full h-8 px-2 text-xs rounded-sm font-['IBM_Plex_Sans'] focus:outline-none focus:border-[#3B82F6] transition-colors ${
                            currentCol ? (isAutoDetected ? 'bg-[#3B82F6]/8 border border-[#3B82F6]/30 text-[#3B82F6]' : 'bg-[#0A0F1A] border border-[#57EB31]/40 text-[#57EB31]')
                            : 'bg-[#0A0F1A] border border-[#2D3F5E] text-[#5C6A7A]'}`}>
                          <option value="">— not mapped —</option>
                          {tabInfo.headers.map(h => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex gap-3">
            <button onClick={() => setStep('preview')}
              className="flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
              <Eye size={15} /> Preview Data
            </button>
          </div>
        </div>
      )}

      {/* ── STEP: PREVIEW ─────────────────────────────────────────────────── */}
      {step === 'preview' && parsedData && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm">
              Review sample data and validation. Fix any errors before importing.
            </p>
            <button onClick={() => setStep('mapping')} className="text-xs text-[#5C6A7A] hover:text-[#3B82F6] font-['JetBrains_Mono']">← Back to Mapping</button>
          </div>

          {/* Validation summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {getValidationSummary().map(({ tabName, rows, hasErrors, errors: errs }) => (
              <div key={tabName} className={`card-industrial p-3 ${hasErrors ? 'border-red-800/50' : ''}`}>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-1">{TAB_LABELS[tabName]}</p>
                <p className="font-['JetBrains_Mono'] text-lg font-bold text-white">{fmt(rows)}</p>
                <p className="text-[10px] font-['IBM_Plex_Sans'] mt-1">rows</p>
                {hasErrors && errs.map((e, i) => (
                  <p key={i} className="text-[10px] text-red-400 font-['IBM_Plex_Sans'] mt-1">{e}</p>
                ))}
                {!hasErrors && <p className="text-[10px] text-[#57EB31] font-['IBM_Plex_Sans'] mt-1">✓ Ready</p>}
              </div>
            ))}
          </div>

          {/* Sample rows per tab */}
          {tabsPresent.map(tabName => {
            const tab = parsedData.tabs[tabName];
            if (!tab.sample_rows?.length) return null;
            return (
              <div key={tabName} className="card-industrial overflow-hidden">
                <div className="px-5 py-3 border-b border-[#2D3F5E] flex items-center gap-3">
                  <p className="text-white font-['Manrope'] font-bold text-sm">{TAB_LABELS[tabName]}</p>
                  <span className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A]">First {tab.sample_rows.length} rows (of {tab.total_rows})</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="text-[10px] font-['JetBrains_Mono'] min-w-full">
                    <thead><tr className="border-b border-[#2D3F5E] bg-[#0A0F1A]">
                      {tab.headers.map(h => <th key={h} className="text-left px-3 py-2 text-[#5C6A7A] uppercase whitespace-nowrap">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {tab.sample_rows.map((row, i) => (
                        <tr key={i} className="border-b border-[#2D3F5E]/30" style={{ background: i%2!==0?'rgba(255,255,255,0.01)':'transparent' }}>
                          {tab.headers.map(h => (
                            <td key={h} className="px-3 py-2 text-[#B8C0CC] whitespace-nowrap max-w-[160px] truncate">{String(row[h] ?? '')}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button onClick={() => handleRunJob(true)}
              className="flex items-center gap-2 px-5 py-2.5 border border-blue-700/50 text-blue-400 rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-blue-900/20 transition-colors"
              data-testid="dry-run-btn">
              <Eye size={15} /> Dry Run (no changes)
            </button>
            <button onClick={() => handleRunJob(false)}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors shadow-[0_0_20px_rgba(59,130,246,0.2)]"
              data-testid="run-import-btn">
              <Play size={15} /> Run Import
            </button>
          </div>
        </div>
      )}

      {/* ── STEP: RUNNING ─────────────────────────────────────────────────── */}
      {step === 'running' && (
        <div className="max-w-lg mx-auto text-center py-16 space-y-6">
          <Loader2 size={40} className="text-[#3B82F6] animate-spin mx-auto" />
          <div>
            <p className="text-white font-['Manrope'] font-bold text-lg mb-2">
              {jobData?.is_dry_run ? 'Running Dry Run...' : 'Importing Data...'}
            </p>
            <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">{jobData?.progress_message || 'Starting...'}</p>
          </div>
          {jobData?.progress != null && (
            <div className="w-full bg-[#0F172A] rounded-full h-2 max-w-xs mx-auto">
              <div className="bg-[#3B82F6] h-2 rounded-full transition-all duration-500" style={{ width: `${jobData.progress}%` }} />
            </div>
          )}
          <p className="text-[10px] text-[#2D3F5E] font-['JetBrains_Mono']">{jobData?.progress || 0}%</p>
        </div>
      )}

      {/* ── STEP: RESULTS ─────────────────────────────────────────────────── */}
      {step === 'results' && jobData && (
        <div className="space-y-5">
          <div className="flex items-center gap-4">
            <div className={`text-2xl font-['Manrope'] font-extrabold ${PAYMENT_COLORS[jobData.status] || 'text-white'}`}>
              {jobData.is_dry_run ? 'Dry Run Complete' : (jobData.status === 'SUCCESS' ? 'Import Complete!' : jobData.status)}
            </div>
            {jobData.is_dry_run && (
              <span className="px-3 py-1 bg-blue-900/20 border border-blue-800/50 rounded-full text-xs text-blue-400 font-['JetBrains_Mono'] uppercase">
                No data written
              </span>
            )}
          </div>

          {/* Per-tab summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(jobData.summary || {}).map(([tabName, s]) => (
              <div key={tabName} className="card-industrial p-4">
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] mb-3">{TAB_LABELS[tabName] || tabName}</p>
                <div className="space-y-1.5">
                  {[['Created', 'created', 'text-[#57EB31]'], ['Updated', 'updated', 'text-[#3B82F6]'], ['Skipped', 'skipped', 'text-[#5C6A7A]'], ['Errors', 'errors', 'text-red-400']].map(([label, key, color]) => (
                    <div key={key} className="flex justify-between text-xs font-['JetBrains_Mono']">
                      <span className="text-[#5C6A7A]">{label}</span>
                      <span className={`font-bold ${color}`}>{s[key] || 0}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Errors list */}
          {jobData.errors?.length > 0 && (
            <div className="card-industrial p-5">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-red-400 mb-3">Errors ({jobData.errors.length})</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {jobData.errors.map((e, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs font-['IBM_Plex_Sans']">
                    <XCircle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-[#5C6A7A]">Row {e.row} ({e.tab}): <span className="text-red-300">{e.error}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {jobData.warnings?.length > 0 && (
            <div className="card-industrial p-4">
              <p className="text-[9px] font-['JetBrains_Mono'] uppercase text-[#3B82F6] mb-2">Warnings ({jobData.warnings.length})</p>
              {jobData.warnings.slice(0, 5).map((w, i) => (
                <p key={i} className="text-xs text-[#8B9AAB] font-['IBM_Plex_Sans']"><AlertTriangle size={10} className="inline mr-1 text-[#3B82F6]" />{w.warning}</p>
              ))}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-6 text-xs text-[#5C6A7A] font-['IBM_Plex_Sans']">
            <span>By: {jobData.imported_by_name}</span>
            <span>{jobData.completed_at ? new Date(jobData.completed_at).toLocaleString() : ''}</span>
            {jobData.source_file_key && <span>File stored in R2: {jobData.source_file_key}</span>}
          </div>

          <div className="flex gap-3">
            {jobData.is_dry_run && (
              <button onClick={() => { setStep('preview'); }}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB]">
                <Play size={14} /> Run Real Import
              </button>
            )}
            <button onClick={reset} className="flex items-center gap-2 px-4 py-2.5 border border-[#2D3F5E] text-[#B8C0CC] rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:border-[#3B82F6]/50 hover:text-[#3B82F6] transition-colors">
              <RefreshCw size={14} /> New Import
            </button>
          </div>
        </div>
      )}

      {/* Import History Modal */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="bg-[#1A2332] border-[#2D3F5E] max-w-3xl">
          <DialogHeader><DialogTitle className="text-white font-['Manrope']">Import History</DialogTitle></DialogHeader>
          <div className="mt-2 space-y-2 max-h-96 overflow-y-auto">
            {history.length === 0 ? (
              <p className="text-[#5C6A7A] text-center py-8 font-['IBM_Plex_Sans']">No imports yet</p>
            ) : history.map(job => (
              <div key={job.id} className="p-3 border border-[#2D3F5E] rounded-sm">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold font-['JetBrains_Mono'] uppercase ${PAYMENT_COLORS[job.status] || 'text-white'}`}>{job.status}</span>
                    <span className="text-sm text-white font-['IBM_Plex_Sans']">{job.customer_name}</span>
                    {job.is_dry_run && <span className="text-[9px] text-blue-400 border border-blue-800/40 px-1.5 py-0.5 rounded font-['JetBrains_Mono']">DRY RUN</span>}
                  </div>
                  <span className="text-xs text-[#5C6A7A] font-['JetBrains_Mono']">{job.created_at ? new Date(job.created_at).toLocaleDateString() : ''}</span>
                </div>
                {job.summary && (
                  <div className="flex gap-4 text-[10px] font-['JetBrains_Mono']">
                    {Object.entries(job.summary).map(([tab, s]) => (
                      <span key={tab} className="text-[#5C6A7A]">
                        {tab}: <span className="text-[#57EB31]">{s.created}↑</span> <span className="text-[#3B82F6]">{s.updated}✎</span> <span className="text-[#5C6A7A]">{s.skipped}—</span>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-1">By {job.imported_by_name} · {job.source_type}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
